import React, { useEffect, useMemo, useRef } from 'react';
import type { EChartsOption } from 'echarts';

import echarts from 'components/StudioCharts/echarts';
import { useRegionMaps } from 'components/StudioCharts/geo/regionMaps';
import toEChartsOption, {
  getAxisLabelLayout,
  getValueTickCount,
} from 'components/StudioCharts/toEChartsOption';
import useChartTheme from 'components/StudioCharts/useChartTheme';

import { PREVIEW_CHART_SIZE, isReducedMotion } from './constants';
import { SAMPLE_METRICS, getSampleChart } from './sampleModels';

// The sample's values in its made-up metrics' formats ($182, 94%)
const formatMetric = ({
  metricId,
  value,
}: {
  metricId: string;
  value: string | number | null | undefined;
}) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const format = SAMPLE_METRICS[metricId] ?? {};
  return `${format.prefix ?? ''}${value}${format.suffix ?? ''}`;
};

// Drawn slower than a chart on the canvas, each item a beat after the one
// before, so the preview builds up as it opens
const withEntrance = (option: EChartsOption): EChartsOption => ({
  ...option,
  animation: !isReducedMotion(),
  animationDuration: 900,
  animationEasing: 'cubicOut',
  animationDelay: (index: number) => index * 60,
  tooltip: { show: false },
});

// The sample drawn once, at its fixed size. Not with EChart: its resizing
// on mount ends the entrance before it's seen, and the preview takes no
// clicks.
const PreviewEChart = ({
  option,
  width,
  height,
  testId,
}: {
  option: EChartsOption;
  width: number;
  height: number;
  testId: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }
    const instance = echarts.init(ref.current, undefined, {
      renderer: 'svg',
      width,
      height,
    });
    instance.setOption(option);
    return () => instance.dispose();
  }, [height, option, width]);
  return <div ref={ref} data-testid={testId} style={{ width, height }} />;
};

// A vis type's sample, drawn by Studio's own ECharts renderer: the same
// options, theme and colours as a chart on the canvas
const SampleChart = ({
  visType,
  isLegendHidden = false,
  width = PREVIEW_CHART_SIZE.width,
  height = PREVIEW_CHART_SIZE.height,
  hasEntrance = true,
}: {
  visType: Studio.VisType;
  // As a Composite's chart, whose Numbers stand for its series
  isLegendHidden?: boolean;
  // The room it's drawn in, if not a preview's
  width?: number;
  height?: number;
  // Off where it's redrawn as it resizes (a placeholder gadget), so it
  // doesn't replay each time
  hasEntrance?: boolean;
}) => {
  const theme = useChartTheme();
  const sample = useMemo(
    () => getSampleChart({ visType, theme }),
    [theme, visType],
  );
  // A Map waits for its outlines, loaded the first time one draws
  const isRegionMapsReady = useRegionMaps(
    !!sample && sample.model.kind === 'regionMap',
  );

  const option = useMemo(() => {
    if (!sample) {
      return undefined;
    }
    const model: StudioCharts.Model = {
      ...sample.model,
      ...(sample.style ? { style: sample.style } : {}),
      ...(isLegendHidden ? { showLegend: false } : {}),
    };
    const chartOption = toEChartsOption({
      model,
      theme,
      formatMetric,
      labelLayout:
        model.kind === 'cartesian'
          ? getAxisLabelLayout({ model, width })
          : undefined,
      tickCount:
        model.kind === 'cartesian'
          ? getValueTickCount({
              isHorizontal: model.isHorizontal,
              width,
              height: height - 30 - (model.showLegend ? 32 : 0),
            })
          : undefined,
      size: { width, height },
    });
    return hasEntrance
      ? withEntrance(chartOption)
      : { ...chartOption, animation: false, tooltip: { show: false } };
  }, [hasEntrance, height, isLegendHidden, sample, theme, width]);

  if (
    !sample ||
    !option ||
    (sample.model.kind === 'regionMap' && !isRegionMapsReady)
  ) {
    return null;
  }
  return (
    <PreviewEChart
      option={option}
      width={width}
      height={height}
      testId={`studio-vis-type-sample-${visType}`}
    />
  );
};

export default SampleChart;
